import { supabase } from "@fieldbook/common/supabase"
import { useQueryClient } from "@tanstack/react-query"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  clearOfflineAuthSnapshotBestEffort,
  writeOfflineAuthSnapshot,
} from "@/lib/offlineAuth"

type AuthProviderProps = {
  children: React.ReactNode
}

type AuthProviderState = {
  isLoggedIn: boolean
  setIsLoggedIn: (isLoggedIn: boolean) => void
}

const initialState: AuthProviderState = {
  isLoggedIn: false,
  setIsLoggedIn: (_: boolean) => {},
}

const AuthProviderContext = createContext<AuthProviderState>(initialState)

export function AuthProvider({ children, ...props }: AuthProviderProps) {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const queryClient = useQueryClient()
  const updateIsLoggedIn = useCallback((nextIsLoggedIn: boolean) => {
    setIsLoggedIn((previousIsLoggedIn) =>
      previousIsLoggedIn === nextIsLoggedIn
        ? previousIsLoggedIn
        : nextIsLoggedIn,
    )
  }, [])

  useEffect(() => {
    // Mirror the Supabase session into the query cache on every auth
    // event (TOKEN_REFRESHED carries updated app_metadata claims from
    // the custom_access_token_hook, so org/role stay current without a
    // separate query). useAuth derives the startup state from this cached
    // session so router, auth, and sync share one bootstrap result.
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      queryClient.setQueryData(["auth", "session"], session)
      // getSessionWithOfflineFallback also writes snapshots; duplicate writes
      // are intentional and preserve the original login timestamp.
      if (session) void writeOfflineAuthSnapshot(session)
      if (event === "SIGNED_OUT") void clearOfflineAuthSnapshotBestEffort()
    })
    return () => data?.subscription.unsubscribe()
  }, [queryClient])

  return (
    <AuthProviderContext.Provider
      {...props}
      value={{
        isLoggedIn,
        setIsLoggedIn: updateIsLoggedIn,
      }}
    >
      {children}
    </AuthProviderContext.Provider>
  )
}

export const useAuthContext = () => {
  const context = useContext(AuthProviderContext)

  if (context === undefined)
    throw new Error("useAuthContext must be used within a AuthProvider")

  return context
}
